<script lang="ts">
  /**
   * BetaSheet — opened by the header's "Beta" pill (lane beta-panel). One
   * screen, in order: which build you're on, Send feedback, Check your setup,
   * Copy diagnostics. Data: the shell's `betaDiagnostics` (App.svelte loads it
   * on open) or a named fixture. Send feedback calls `sendFeedback`; the Invite
   * fix opens the native club sheet. Every tap is also recorded on
   * `window.__betaLastAction` for the stills harness.
   *
   * Mounted by App.svelte OUTSIDE #app-shell, same shape as MenuSheet: scrim,
   * grabber + Done head, Escape, drag down (sheetDrag), single overlay slot.
   */
  import { closeOverlay } from "../overlay";
  import { haptic } from "../haptics";
  import { lockBodyScroll, unlockBodyScroll } from "../native/bodyScrollLock";
  import { sheetDrag } from "../sheetDrag";
  import { bridge } from "../../bridge/client";
  import { supportsMethod } from "../nativeMethods";
  import { feedbackBody, formatDiagnostics, healthFixAction, setupRows, shortDay, type BetaDiagnostics, type SetupFix } from "./betaPanel";

  let {
    open = false,
    diagnostics,
    failed = false,
    onRefresh = () => {},
  }: { open?: boolean; diagnostics: BetaDiagnostics | null; failed?: boolean; onRefresh?: () => void } = $props();

  /** Rows whose "How to" text is expanded. */
  let expanded = $state<Record<string, boolean>>({});
  $effect(() => {
    if (!open) expanded = {};
  });

  $effect(() => {
    if (!open) return;
    lockBodyScroll();
    return () => unlockBodyScroll();
  });

  let copied = $state(false);
  $effect(() => {
    if (!open) copied = false;
  });

  const rows = $derived(diagnostics ? setupRows(diagnostics) : []);
  const allOk = $derived(rows.length > 0 && rows.every((r) => r.ok));

  function close() {
    closeOverlay("beta");
  }

  function record(action: string) {
    (window as unknown as Record<string, unknown>).__betaLastAction = action;
  }

  function onFeedback() {
    haptic("light");
    record("feedback");
    if (diagnostics && supportsMethod("sendFeedback")) {
      void bridge.sendFeedback(feedbackBody(diagnostics)).catch(() => {});
    }
  }

  // Every fix does something (orchestrator ruling, 2026-09-12: no dead buttons).
  // The recorded tap is for the stills harness, never the whole action.
  async function onFix(key: string, fix: SetupFix) {
    haptic("light");
    record(`fix:${key}`);
    switch (fix) {
      case "How to":
        expanded = { ...expanded, [key]: !expanded[key] };
        return;
      case "Turn on":
        if (diagnostics && healthFixAction(diagnostics.health) === "settings") {
          if (supportsMethod("openSettings")) void bridge.openSettings();
          return;
        }
        if (!supportsMethod("requestHealthAccess")) return;
        try {
          await bridge.requestHealthAccess();
        } finally {
          onRefresh();
        }
        return;
      case "Settings":
        if (supportsMethod("openSettings")) void bridge.openSettings();
        return;
      case "Invite":
      case "Club settings":
        if (supportsMethod("openClubSettings")) void bridge.openClubSettings();
        return;
    }
  }

  /** navigator.clipboard first; WKWebView file:// pages can refuse it, so fall back to execCommand. */
  async function copyText(text: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      const area = document.createElement("textarea");
      area.value = text;
      area.setAttribute("readonly", "");
      area.style.position = "fixed";
      area.style.opacity = "0";
      document.body.appendChild(area);
      area.select();
      const ok = document.execCommand("copy");
      area.remove();
      return ok;
    }
  }

  async function onCopy() {
    if (!diagnostics) return;
    haptic("light");
    record("copy");
    copied = await copyText(formatDiagnostics(diagnostics));
  }

  function onKey(e: KeyboardEvent) {
    if (open && e.key === "Escape") close();
  }
</script>

<svelte:window onkeydown={onKey} />

{#if open}
  <div class="scrim" role="presentation" onclick={close}></div>
  <div class="sheet" role="dialog" aria-modal="true" aria-label="Beta" use:sheetDrag={close}>
    <div class="head">
      <div class="grabber" aria-hidden="true"></div>
      <button type="button" class="done" onclick={close}>Done</button>
    </div>

    {#if !diagnostics}
      <p class="hint" data-beta={failed ? "failed" : "loading"}>
        {failed ? "Couldn't check your setup just now. Close this and try again." : "Checking your setup…"}
      </p>
    {:else}
    <!-- a. Which build -->
    <section class="build" data-beta="build">
      <h2>You're on beta build {diagnostics.build.number}</h2>
      {#if diagnostics.build.date}
        <p class="date">Released {shortDay(diagnostics.build.date)}</p>
      {/if}
      {#if diagnostics.build.whatsNew}
        <ul class="new">
          {#each diagnostics.build.whatsNew as line}
            <li>{line}</li>
          {/each}
        </ul>
      {/if}
    </section>

    <!-- b. Send feedback -->
    <section data-beta="feedback">
      <button type="button" class="primary" onclick={onFeedback}>Send feedback</button>
      <p class="hint">No Mail app? Write to support@alliteratelabs.com, or take a screenshot, tap Share, choose TestFlight</p>
    </section>

    <!-- c. Check your setup -->
    <section data-beta="setup">
      <h3>Check your setup</h3>
      <ul class="setup">
        {#each rows as row (row.key)}
          <li class="setup-row" class:bad={!row.ok} data-setup={row.key} data-ok={row.ok ? "1" : "0"}>
            <span class="mark" aria-hidden="true">
              {#if row.ok}
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="5 12.5 10 17 19 7"/></svg>
              {:else}
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M12 6v8M12 18h.01"/></svg>
              {/if}
            </span>
            <span class="body">
              <span class="title">{row.title}<span class="sr">{row.ok ? ", working" : ", needs a fix"}</span></span>
              <span class="detail" class:neutral={row.alert}>{row.detail}</span>
              {#if row.alert}
                <span class="alert" data-alert={row.key}>{row.alert}</span>
              {/if}
              {#if row.help && expanded[row.key]}
                <span class="help" data-help={row.key}>{row.help}</span>
              {/if}
            </span>
            {#if row.fix}
              <button
                type="button"
                class="fix"
                aria-expanded={row.fix === "How to" ? Boolean(expanded[row.key]) : undefined}
                onclick={() => onFix(row.key, row.fix!)}
              >{row.fix === "How to" && expanded[row.key] ? "Hide" : row.fix}</button>
            {/if}
          </li>
        {/each}
      </ul>
      {#if allOk}
        <p class="hint" data-beta="all-ok">These look good. Still stuck? Send feedback.</p>
      {:else if rows.length > 0}
        <!-- VP copy round 2 (4 of 5): the red state needs a way out in the same spot. -->
        <p class="hint" data-beta="stuck">Still stuck? Send feedback and we'll take a look.</p>
      {/if}
    </section>

    <!-- d. Copy diagnostics -->
    <section data-beta="copy">
      <button type="button" class="secondary" onclick={onCopy}>{copied ? "Copied" : "Copy diagnostics"}</button>
      <p class="hint">Your app and phone details. No names or swims.</p>
    </section>
    {/if}
  </div>
{/if}

<style>
  .scrim {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 50;
    animation: scrim-in var(--dur-card) var(--ease-swim);
    touch-action: none;
  }
  .sheet {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 51;
    display: flex;
    flex-direction: column;
    /* 16, not 20: four setup rows must still fit one 390x844 screen (ruling A). */
    gap: var(--space-4);
    /* Opaque, unlike the other glass sheets: this one is all text, and the 42%
       Dawn Glass card let the swim page's own words show through under the
       last line (VP round 1, reviewer; confirmed on the still). */
    background-color: var(--bg-canvas);
    background-image: linear-gradient(var(--bg-card), var(--bg-card));
    border-top: 1px solid var(--divider);
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;
    padding: var(--space-3) var(--space-4)
      calc(var(--space-6) + env(safe-area-inset-bottom, 0px));
    box-shadow: var(--shadow-floating);
    backdrop-filter: blur(24px) saturate(1.2);
    -webkit-backdrop-filter: blur(24px) saturate(1.2);
    animation: sheet-in var(--dur-card) var(--ease-swim);
    max-height: calc(100vh - 48px - env(safe-area-inset-top, 0px));
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-y; /* NEVER touch-action: none (WKWebView trap) */
    overscroll-behavior: contain;
  }
  section {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .head {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    min-height: 44px;
    margin: calc(-1 * var(--space-3)) 0 calc(-1 * var(--space-4));
  }
  .grabber {
    position: absolute;
    top: 10px;
    left: 50%;
    width: 36px;
    height: 4px;
    margin-left: -18px;
    border-radius: 999px;
    background: var(--divider-strong);
  }
  .done {
    min-height: 44px;
    padding: 0 var(--space-2);
    margin-right: calc(-1 * var(--space-2));
    border: 0;
    background: transparent;
    color: var(--accent-primary);
    font: inherit;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
  }
  h2 {
    margin: 0;
    font: var(--type-display-mobile);
    color: var(--text-primary);
  }
  h3 {
    margin: 0;
    font: var(--type-title-subtitle);
    font-weight: 600;
    color: var(--text-primary);
  }
  .date {
    margin: 0;
    font: var(--type-body-sm);
    color: var(--text-muted);
  }
  .new {
    margin: var(--space-1) 0 0;
    padding-left: var(--space-5);
    font: var(--type-body);
    color: var(--text-body);
  }
  .primary,
  .secondary,
  .fix {
    min-height: 48px;
    border-radius: var(--radius-button);
    font: inherit;
    font-size: 17px;
    font-weight: 600;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .primary {
    width: 100%;
    border: 0;
    background: var(--accent-primary);
    color: var(--bg-canvas);
  }
  .secondary {
    width: 100%;
    border: 1px solid var(--divider-strong);
    background: var(--bg-surface);
    color: var(--text-primary);
  }
  .primary:focus-visible,
  .secondary:focus-visible,
  .fix:focus-visible,
  .done:focus-visible {
    outline: 2px solid var(--accent-yellow);
    outline-offset: 2px;
  }
  .hint {
    margin: 0;
    font: var(--type-body-sm);
    color: var(--text-muted);
    text-align: center;
  }
  .setup {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .setup-row {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: 10px var(--space-3);
    background: var(--bg-surface);
    border: 1px solid var(--divider);
    border-radius: var(--radius-card);
  }
  .setup-row.bad {
    border-color: var(--accent-error);
  }
  .mark {
    display: inline-flex;
    flex: 0 0 auto;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    align-items: center;
    justify-content: center;
    background: var(--win);
    color: var(--bg-canvas);
  }
  .bad .mark {
    background: var(--accent-error);
  }
  .body {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    flex: 1 1 auto;
  }
  .title {
    font: var(--type-title-subtitle);
    color: var(--text-primary);
  }
  .detail {
    font: var(--type-body-sm);
    color: var(--text-body);
  }
  .bad .detail {
    color: var(--accent-error);
  }
  .bad .detail.neutral {
    color: var(--text-body);
  }
  .alert {
    font: var(--type-body-sm);
    color: var(--accent-error);
  }
  .help {
    margin-top: var(--space-1);
    font: var(--type-body-sm);
    color: var(--text-body);
  }
  .fix {
    flex: 0 0 auto;
    min-height: 44px;
    padding: 0 var(--space-4);
    border: 0;
    background: var(--accent-error);
    color: var(--bg-canvas);
    font-size: 16px;
  }
  .sr {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }
  @keyframes scrim-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes sheet-in {
    from { transform: translateY(100%); }
    to   { transform: translateY(0); }
  }
</style>
